/**
 * Comic-book VFX: word pops, dust puffs, water droplets.
 *
 * This layer carries a disproportionate share of the game's perceived polish and
 * needs ZERO 3D assets - every texture here is drawn procedurally on a canvas at
 * startup. That is why it gets built early: while every model is still a grey
 * capsule, a "CRASH!" flying off a shattered vase already makes the game feel
 * alive and funny.
 *
 * All textures are cached and shared. Particle counts scale with the quality
 * tier's vfxDensity so the low tier stays cheap.
 */

import * as THREE from 'three';
import type { Vec3 } from '@/core/math';
import { clamp01 } from '@/core/math';

interface Particle {
  sprite: THREE.Sprite;
  age: number;
  lifetime: number;
  velocity: THREE.Vector3;
  gravity: number;
  startScale: number;
  endScale: number;
  spin: number;
}

const wordTextures = new Map<string, THREE.Texture>();
let puffTexture: THREE.Texture | null = null;

/**
 * Draws a chunky comic word onto a canvas: fat sans-serif, thick black stroke,
 * slight rotation. Cached per text+color combination.
 */
function getWordTexture(text: string, color: string): THREE.Texture {
  const key = `${text}|${color}`;
  const cached = wordTextures.get(key);
  if (cached) return cached;

  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size / 2;
  const ctx = canvas.getContext('2d');

  if (ctx) {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Impact-style stack: heavy, condensed, reads at small sizes.
    ctx.font = '900 76px "Arial Black", Impact, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';

    const x = canvas.width / 2;
    const y = canvas.height / 2;

    // Thick ink outline first, then the fill - matches the art bible's outlines.
    ctx.strokeStyle = '#14101a';
    ctx.lineWidth = 14;
    ctx.strokeText(text, x, y);
    ctx.fillStyle = color;
    ctx.fillText(text, x, y);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  wordTextures.set(key, texture);
  return texture;
}

/** Soft radial puff, drawn once and reused for dust, smoke and water. */
function getPuffTexture(): THREE.Texture {
  if (puffTexture) return puffTexture;

  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  if (ctx) {
    const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    gradient.addColorStop(0, 'rgba(255,255,255,1)');
    gradient.addColorStop(0.55, 'rgba(255,255,255,0.65)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
  }

  puffTexture = new THREE.CanvasTexture(canvas);
  return puffTexture;
}

export class VfxSystem {
  private particles: Particle[] = [];
  private density = 1;

  constructor(private readonly scene: THREE.Scene) {}

  /** Set from the active quality tier's vfxDensity. */
  setDensity(density: number): void {
    this.density = clamp01(density);
  }

  /**
   * A comic word pop: "CRASH!", "POP!", "MEOW!".
   * Pops to full size fast, then drifts up and fades - the timing is what makes
   * it read as a cartoon rather than a floating label.
   */
  pop(position: Vec3, text: string, color = '#ffd23f'): void {
    const material = new THREE.SpriteMaterial({
      map: getWordTexture(text.toUpperCase(), color),
      transparent: true,
      depthWrite: false,
      // Draw over geometry: a pop hidden behind a couch is a wasted joke.
      depthTest: false,
    });

    const sprite = new THREE.Sprite(material);
    // Jitter the spawn point. Several pops routinely fire at the same spot on the
    // same frame (a crash also reveals a key, which also gets collected), and
    // perfectly stacked words are unreadable.
    sprite.position.set(
      position.x + (Math.random() - 0.5) * 0.3,
      position.y + 0.25 + Math.random() * 0.2,
      position.z + (Math.random() - 0.5) * 0.3,
    );
    sprite.renderOrder = 100;
    sprite.scale.setScalar(0.01);
    this.scene.add(sprite);

    this.particles.push({
      sprite,
      age: 0,
      lifetime: 0.9,
      velocity: new THREE.Vector3(0, 1.1, 0),
      gravity: 0,
      startScale: 0.7,
      endScale: 0.55,
      spin: 0,
    });
  }

  /** Dust puffs, for landings, impacts and toppling furniture. */
  dust(position: Vec3, amount = 6): void {
    const count = Math.max(1, Math.round(amount * this.density));
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.5;
      const speed = 0.6 + Math.random() * 0.8;

      const material = new THREE.SpriteMaterial({
        map: getPuffTexture(),
        transparent: true,
        depthWrite: false,
        color: new THREE.Color('#e8dcc8'),
        opacity: 0.75,
      });

      const sprite = new THREE.Sprite(material);
      sprite.position.set(position.x, position.y + 0.05, position.z);
      this.scene.add(sprite);

      this.particles.push({
        sprite,
        age: 0,
        lifetime: 0.5 + Math.random() * 0.3,
        velocity: new THREE.Vector3(
          Math.cos(angle) * speed,
          0.35 + Math.random() * 0.3,
          Math.sin(angle) * speed,
        ),
        gravity: -0.8,
        startScale: 0.08,
        endScale: 0.42,
        spin: 0,
      });
    }
  }

  /** Water droplets, for granny's spray bottle and the flooding bathroom. */
  splash(position: Vec3, direction: Vec3, amount = 10): void {
    const count = Math.max(1, Math.round(amount * this.density));
    for (let i = 0; i < count; i++) {
      const material = new THREE.SpriteMaterial({
        map: getPuffTexture(),
        transparent: true,
        depthWrite: false,
        color: new THREE.Color('#7fd4e8'),
        opacity: 0.9,
      });

      const sprite = new THREE.Sprite(material);
      sprite.position.set(position.x, position.y, position.z);
      this.scene.add(sprite);

      const spread = 0.45;
      this.particles.push({
        sprite,
        age: 0,
        lifetime: 0.4 + Math.random() * 0.25,
        velocity: new THREE.Vector3(
          direction.x * 3 + (Math.random() - 0.5) * spread * 3,
          direction.y * 3 + Math.random() * 0.6,
          direction.z * 3 + (Math.random() - 0.5) * spread * 3,
        ),
        gravity: -5,
        startScale: 0.05,
        endScale: 0.02,
        spin: 0,
      });
    }
  }

  /**
   * Advances every particle. Takes the REAL frame delta, not the fixed
   * simulation step - VFX are presentation-only and must not affect the sim.
   */
  update(realDt: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      if (!p) continue;

      p.age += realDt;
      const t = p.age / p.lifetime;

      if (t >= 1) {
        this.scene.remove(p.sprite);
        p.sprite.material.dispose();
        this.particles.splice(i, 1);
        continue;
      }

      p.velocity.y += p.gravity * realDt;
      p.sprite.position.addScaledVector(p.velocity, realDt);

      // Overshoot-and-settle scale curve: a fast pop past the target size then a
      // settle back. This is the difference between "appears" and "POPS".
      const eased = t < 0.25 ? easeOutBack(t / 0.25) : 1;
      const scale = p.startScale + (p.endScale - p.startScale) * t;
      p.sprite.scale.setScalar(scale * eased);

      // Hold full opacity for the first 60% of life, then fade.
      const material = p.sprite.material;
      material.opacity = t < 0.6 ? 1 : 1 - (t - 0.6) / 0.4;

      if (p.spin !== 0) p.sprite.material.rotation += p.spin * realDt;
    }
  }

  /** Level teardown. */
  clear(): void {
    for (const p of this.particles) {
      this.scene.remove(p.sprite);
      p.sprite.material.dispose();
    }
    this.particles.length = 0;
  }

  get particleCount(): number {
    return this.particles.length;
  }
}

/** Overshoots past 1 then settles - the classic cartoon "pop". */
function easeOutBack(t: number): number {
  const c = 2.2;
  const p = t - 1;
  return 1 + (c + 1) * p * p * p + c * p * p;
}

export function disposeVfxTextures(): void {
  for (const texture of wordTextures.values()) texture.dispose();
  wordTextures.clear();
  puffTexture?.dispose();
  puffTexture = null;
}
